import React from "react";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaOptionsType } from "embla-carousel";
import "./styles.less";
import { RightArrowIcon } from "shared-components/Components/CustomIcons/RightArrowIcon/web";
import { LeftArrowIcon } from "shared-components/Components/CustomIcons/LeftArrowIcon/web";
import { NextButton, PrevButton, usePrevNextButtons } from "./EmblaArrows";
import { usePagination } from "./EmblaPagination";
import Link from "next/link";

type NewEmblaOptionsType = EmblaOptionsType & {
	slidesPerView?: number;
	navigation?: boolean;
	pagination?: boolean;
};

type EmblaTypes = {
	options?: NewEmblaOptionsType;
	children: React.ReactNode;
	title: string;
	className?: string;
	totalSlides?: number;
	link?: string;
	target?: string;
	rel?: string;
	widthSlide?: string;
	fullWidth?: boolean;
	hasflag?: boolean;
	handleClick?: React.MouseEventHandler<HTMLAnchorElement>;
};


export const EmblaCarousel = ({
	options = {
		inViewThreshold: 0, // opcional: 0–1 suele ser lo esperado
	},
	children,
	title,
	className,
}: EmblaTypes) => {
	const [emblaRef, emblaApi] = useEmblaCarousel({
		axis: "x",
		direction: "ltr",
		containScroll: "trimSnaps",
		watchDrag: true,     // <– habilita drag (mouse/touch)
		watchResize: true,
		watchSlides: true,
		watchFocus: true,
		...options,
	});

	const {
		onPrevButtonClick,
		onNextButtonClick,
		prevBtnDisabled,
		nextBtnDisabled,
	} = usePrevNextButtons(emblaApi);

	// Evita que un click navegue si realmente fue un swipe/drag
	const handleClickCapture: React.MouseEventHandler<HTMLDivElement> = (e) => {
		if (emblaApi && !emblaApi.clickAllowed()) {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	// Si quieres controlar # de slides por vista desde props (options.slidesPerView)
	// calcula el tamaño y pásalo como CSS variable:
	const slideSize =
		(options as any)?.slidesPerView
			? `${100 / (options as any).slidesPerView}%`
			: undefined;

	// Para pasar CSS custom properties en TS:
	const styleVars: React.CSSProperties & { ["--slide-size"]?: string } = {};
	if (slideSize) styleVars["--slide-size"] = slideSize;

	return (
		<section className={`embla emblaCarousel ${className ?? ""}`} style={styleVars}>
			{options.navigation && (
				<button
					className="embla__button emblaCarousel__navigation emblaCarousel__navigation--left"
					disabled={prevBtnDisabled}
					onClick={onPrevButtonClick}
				>
					<LeftArrowIcon className="icon-arrow" />
				</button>
			)}

			{/* Viewport: aquí va el ref */}
			<div className="embla__viewport" ref={emblaRef} onClickCapture={handleClickCapture}>
				<div className="embla__container">
					{React.Children.map(children, (child, index) => (
						<div
							key={`embla__slide-${title}-${index}`}
							className="embla__slide" // el ancho lo marca --slide-size (CSS)
						>
							{child}
						</div>
					))}
				</div>
			</div>

			{options.navigation && (
				<button
					className="embla__button emblaCarousel__navigation emblaCarousel__navigation--right"
					disabled={nextBtnDisabled}
					onClick={onNextButtonClick}
				>
					<RightArrowIcon className="icon-arrow" />
				</button>
			)}
		</section>
	);
};


export const EmblaGalleryCarousel = ({
	options = {},
	children,
	title,
	className,
	totalSlides,
	link,
	target,
	rel,
	hasflag = false,
	handleClick
}: EmblaTypes) => {
	const [emblaRef, emblaApi] = useEmblaCarousel(options);

	const {
		onPrevButtonClick,
		onNextButtonClick,
		nextBtnDisabled,
		prevBtnDisabled,
	} = usePrevNextButtons(emblaApi);
	const { selectedIndex, onDotButtonClick } = usePagination(emblaApi);

	return (
		<section className={`emblaGalleryCarousel ${className ?? ''}`}>
			{options.navigation && totalSlides > 1 && (
				<PrevButton
					onClick={() => {
						onPrevButtonClick();
						onDotButtonClick(selectedIndex - 1);
					}}
					disabled={prevBtnDisabled}
				/>
			)}

			<Link href={link} passHref>
				<a target={target} rel={rel} onClick={handleClick}>
					<div className="emblaGalleryCarousel__viewport" ref={emblaRef}>
						<div className="emblaGalleryCarousel__container">
							{React.Children.map(children, (child, index) => (
								<div
									key={`emblaGallery__slide-${title}-${index}`}
									className="emblaGalleryCarousel__slide">
									{child}
								</div>
							))}
						</div>
					</div>
				</a>
			</Link>

			{options.navigation && totalSlides > 1 && (
				<NextButton
					onClick={() => {
						onNextButtonClick();
						onDotButtonClick(selectedIndex + 1);
					}}
					disabled={nextBtnDisabled}
				/>
			)}

			{options.pagination && (
				<div className={`emblaGalleryCarousel__pagination ${hasflag ? 'has-flag' : ''}`}>
					<span>
						{selectedIndex + 1} / {totalSlides}
					</span>
				</div>
			)}
		</section>
	);
};

export const EmblaCarouselHome = ({
	options = {
		inViewThreshold: Infinity,
	},
	children,
	title,
	className,
	widthSlide,
	fullWidth
}: EmblaTypes) => {
	const [emblaRef, emblaApi] = useEmblaCarousel(options);
	const {
		onPrevButtonClick,
		onNextButtonClick,
		nextBtnDisabled,
		prevBtnDisabled,
	} = usePrevNextButtons(emblaApi);

	return (
		<div className={`emblaCarouselHome ${className ? className : ''}`}>
			{options.navigation && (
				<button
					className="emblaCarouselHome__navigation emblaCarouselHome__navigation--left embla__button"
					disabled={prevBtnDisabled}
					onClick={onPrevButtonClick}>
					<LeftArrowIcon className="icon-arrow" />
				</button>
			)}
			<div className={`embla ${fullWidth ? 'emblaFullWidth' : ''}`} ref={emblaRef}>
				<div className="embla__container">
					{React.Children.map(children, (child, index) => (
						<div
							key={`embla__slide-${title}-${index}`}
							className="embla__slide"
							style={{ flex: `0 0 ${widthSlide ? widthSlide : "auto"}` }}>
							{child}
						</div>
					))}
				</div>
			</div>
			{options.navigation && (
				<button
					className="emblaCarouselHome__navigation emblaCarouselHome__navigation--right embla__button"
					disabled={nextBtnDisabled}
					onClick={onNextButtonClick}>
					<RightArrowIcon className="icon-arrow" />
				</button>
			)}
		</div>
	);
};

